import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyShopRepository } from "../../repositories/company/company-shop-repository";
import type {
  UpdateShopSettingsRequest,
  UpdateBestsellerRequest,
  ShopManagementDto,
  ShopBannerEntity,
  CreateBannerRequest,
  UpdateBannerRequest,
  DeleteBannerRequest,
  ShopSectionEntity,
  CreateShopSectionRequest,
  UpdateShopSectionRequest,
  UpdateShopSectionOrderRequest,
  ShopHeaderDesign,
  ShopFooterDesign,
  UpdateShopHeaderDesignRequest,
  UpdateShopFooterDesignRequest,
} from "../../types/company-shop";
import type { ProductEntity } from "../../types/product";

export class CompanyShopUsecase {
  constructor(private companyShopRepository: CompanyShopRepository) {}

  async getShopHome(companyId: number): Promise<ShopManagementDto> {
    try {
      return await this.companyShopRepository.getShopHome(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "쇼핑몰 홈을 불러오지 못했습니다.",
        "SHOP_HOME_GET_FAILED",
        error,
      );
    }
  }

  async updateShopSettings(
    companyId: number,
    request: UpdateShopSettingsRequest,
  ): Promise<ShopManagementDto> {
    try {
      return await this.companyShopRepository.updateShopSettings(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "쇼핑몰 설정을 저장하지 못했습니다.",
        "SHOP_SETTINGS_UPDATE_FAILED",
        error,
      );
    }
  }

  async updateBestsellers(
    companyId: number,
    request: UpdateBestsellerRequest,
  ): Promise<ShopManagementDto> {
    try {
      return await this.companyShopRepository.updateBestsellers(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "베스트셀러를 업데이트하지 못했습니다.",
        "BEST_SELLERS_UPDATE_FAILED",
        error,
      );
    }
  }

  async getShopBanners(companyId: number): Promise<ShopBannerEntity[]> {
    try {
      return await this.companyShopRepository.getShopBanners(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "배너를 가져오지 못했습니다.",
        "SHOP_BANNER_GET_FAILED",
        error,
      );
    }
  }

  async createBanner(request: CreateBannerRequest): Promise<ShopBannerEntity> {
    try {
      return await this.companyShopRepository.createBanner(request);
    } catch (error) {
      throw new AppError(
        String(error) || "배너를 생성하지 못했습니다.",
        "BEST_SELLERS_GET_FAILED",
        error,
      );
    }
  }

  async updateBanner(request: UpdateBannerRequest): Promise<ShopBannerEntity> {
    try {
      return await this.companyShopRepository.updateBanner(request);
    } catch (error) {
      throw new AppError(
        String(error) || "배너를 업데이트하지 못했습니다.",
        "BEST_SELLERS_GET_FAILED",
        error,
      );
    }
  }

  async deleteBanner(request: DeleteBannerRequest): Promise<void> {
    try {
      return await this.companyShopRepository.deleteBanner(request);
    } catch (error) {
      throw new AppError(
        String(error) || "배너를 삭제하지 못했습니다.",
        "BANNER_DELETE_FAILED",
        error,
      );
    }
  }

  async getShopProducts(companyId: number): Promise<ProductEntity[]> {
    try {
      return await this.companyShopRepository.getShopProducts(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "상품을 불러오지 못했습니다.",
        "SHOP_PRODUCTS_GET_FAILED",
        error,
      );
    }
  }

  async updateStripBanner(params: {
    companyId: number;
    bannerFile?: File;
    command: { enabled: boolean; link?: string | null };
  }): Promise<ShopManagementDto> {
    try {
      return await this.companyShopRepository.updateStripBanner(params);
    } catch (error) {
      throw new AppError(
        String(error) || "띠배너를 업데이트하지 못했습니다.",
        "STRIP_BANNER_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteStripBanner(companyId: number): Promise<ShopManagementDto> {
    try {
      return await this.companyShopRepository.deleteStripBanner(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "띠배너를 삭제하지 못했습니다.",
        "STRIP_BANNER_DELETE_FAILED",
        error,
      );
    }
  }

  async getShopSections(companyId: number): Promise<ShopSectionEntity[]> {
    try {
      return await this.companyShopRepository.getShopSections(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "섹션 목록을 불러오지 못했습니다.",
        "SHOP_SECTIONS_GET_FAILED",
        error,
      );
    }
  }

  async createShopSection(
    companyId: number,
    request: CreateShopSectionRequest,
  ): Promise<ShopSectionEntity> {
    try {
      return await this.companyShopRepository.createShopSection(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "섹션을 생성하지 못했습니다.",
        "SHOP_SECTION_CREATE_FAILED",
        error,
      );
    }
  }

  async updateShopSection(
    companyId: number,
    sectionId: number,
    request: UpdateShopSectionRequest,
  ): Promise<ShopSectionEntity> {
    try {
      return await this.companyShopRepository.updateShopSection(
        companyId,
        sectionId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "섹션을 수정하지 못했습니다.",
        "SHOP_SECTION_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteShopSection(
    companyId: number,
    sectionId: number,
  ): Promise<void> {
    try {
      return await this.companyShopRepository.deleteShopSection(
        companyId,
        sectionId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "섹션을 삭제하지 못했습니다.",
        "SHOP_SECTION_DELETE_FAILED",
        error,
      );
    }
  }

  async updateShopSectionOrder(
    companyId: number,
    request: UpdateShopSectionOrderRequest,
  ): Promise<ShopSectionEntity[]> {
    try {
      return await this.companyShopRepository.updateShopSectionOrder(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "섹션 순서를 저장하지 못했습니다.",
        "SHOP_SECTION_ORDER_UPDATE_FAILED",
        error,
      );
    }
  }

  async updateShopHeaderDesign(
    companyId: number,
    request: UpdateShopHeaderDesignRequest,
  ): Promise<ShopHeaderDesign> {
    try {
      return await this.companyShopRepository.updateShopHeaderDesign(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "헤더 디자인을 저장하지 못했습니다.",
        "SHOP_HEADER_DESIGN_UPDATE_FAILED",
        error,
      );
    }
  }

  async updateShopFooterDesign(
    companyId: number,
    request: UpdateShopFooterDesignRequest,
  ): Promise<ShopFooterDesign> {
    try {
      return await this.companyShopRepository.updateShopFooterDesign(
        companyId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "푸터 디자인을 저장하지 못했습니다.",
        "SHOP_FOOTER_DESIGN_UPDATE_FAILED",
        error,
      );
    }
  }
}
